import React from "react";
import { Outlet, Route } from "react-router-dom";
import { TopNav } from "../components/Shell";
import { AccessApiProvider, MockAccessApiProvider } from "./hooks/api";

const LazyAccessNav = React.lazy(() => import("./components/AccessNav"));
const LazyGroupDetailsPage = React.lazy(
  () => import("./components/groups/GroupDetailsPage")
);
const LazyGroupListingPage = React.lazy(
  () => import("./components/groups/GroupListingPage")
);
const LazyRoleDetailsPage = React.lazy(
  () => import("./components/roles/RoleDetailsPage")
);
const LazyRoleListingPage = React.lazy(
  () => import("./components/roles/RoleListingPage")
);
const LazyUserDetailsPage = React.lazy(
  () => import("./components/users/UserDetailsPage")
);
const LazyUserListingPage = React.lazy(
  () => import("./components/users/UserListingPage")
);

/**
 * The AccessContext wraps all routes with an access API provider.
 * This enables these pages to use the access queries & mutations.
 */
export function AccessContext({ useMocks }: { useMocks: boolean }) {
  let ApiProvider: typeof AccessApiProvider;

  // Use the NODE_ENV to allow dead code removal to remove the mock service from the production build.
  if (process.env.NODE_ENV != "test") {
    ApiProvider = AccessApiProvider;
  } else {
    ApiProvider = useMocks ? MockAccessApiProvider : AccessApiProvider;
  }

  return (
    <ApiProvider>
      <TopNav component={LazyAccessNav} />
      <Outlet />
    </ApiProvider>
  );
}

/**
 * makeRoute returns a route for the access control pages,
 * optionally configured to use the mock access services
 * for testing - either manually, or in unit tests.
 */
export function makeRoute({ useMocks = false }: { useMocks?: boolean } = {}) {
  return (
    <Route path="access" element={<AccessContext useMocks={useMocks} />}>
      <Route path="groups" element={<LazyGroupListingPage />} />
      <Route path="groups/:id" element={<LazyGroupDetailsPage />} />
      <Route path="users" element={<LazyUserListingPage />} />
      <Route path="users/:id" element={<LazyUserDetailsPage />} />
      <Route path="roles" element={<LazyRoleListingPage />} />
      <Route path="roles/:id" element={<LazyRoleDetailsPage />} />
    </Route>
  );
}

export const route = makeRoute();
